/**
 * FTPEVirementValidation - Interface de validation des virements FTPE
 * Permet aux admins de valider les virements reçus et activer les adhésions
 */
import React, { useState, useEffect } from 'react';
import { Landmark, CheckCircle, XCircle, Search, RefreshCw, Clock, Loader2, CheckCheck, Square, CheckSquare, Download } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { getToken } from '../services/tokenService';
import { VirementCard } from './FTPEVirementCard';
import FTPEVirementModals from './FTPEVirementModals';
import FTPEVirementBilan from './FTPEVirementBilan';
import { FtpeCbList } from './paiementsCb/FtpeCbList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FTPEVirementValidation() {
  const [adhesions, setAdhesions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending_virement');
  const [stats, setStats] = useState({ pending: 0, validated: 0, rejected: 0 });
  
  // États pour la sélection en masse
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  
  // Modals
  const [validateModal, setValidateModal] = useState({ open: false, adhesion: null });
  const [rejectModal, setRejectModal] = useState({ open: false, adhesion: null });
  const [detailModal, setDetailModal] = useState({ open: false, adhesion: null });
  
  // Form states
  const [validating, setValidating] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [virementDate, setVirementDate] = useState(new Date().toISOString().split('T')[0]);
  const [virementAmount, setVirementAmount] = useState('200');
  // Auteur du virement (pré-rempli avec le nom de l'adhérent, éditable)
  const [virementAuteur, setVirementAuteur] = useState('');
  // Filtre par date (virement validé ou date de demande)
  const [filterDate, setFilterDate] = useState('');
  // Période d'export CSV (clôture comptable)
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  
  const token = getToken();
  const headers = { Authorization: `Bearer ${token}` };

  // Gestion de la sélection
  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    const pending = adhesions.filter(a => a.payment_status === 'pending_virement');
    setSelectedIds(new Set(pending.map(a => a.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  // Validation en masse
  const handleBulkValidate = async () => {
    if (selectedIds.size === 0) {
      toast.error('Veuillez sélectionner au moins un virement');
      return;
    }

    if (!window.confirm(`Confirmer la validation de ${selectedIds.size} virement(s) ?`)) return;

    setBulkProcessing(true);
    let successCount = 0;
    let errorCount = 0;

    for (const id of selectedIds) {
      try {
        const adh = adhesions.find(a => a.id === id);
        await axios.post(
          `${API_URL}/api/ftpe-adhesion/admin/validate-virement/${id}`,
          {
            virement_date: new Date().toISOString().split('T')[0],
            virement_amount: 200,
            virement_auteur: adh ? (adh.raison_sociale || adh.representant_nom || '') : '',
            notes: `Validation en masse le ${new Date().toLocaleDateString('fr-FR')}`
          },
          { headers }
        );
        successCount++;
      } catch {
        errorCount++;
      }
    }

    setBulkProcessing(false);
    
    if (successCount > 0) {
      toast.success(`✅ ${successCount} virement(s) validé(s) avec succès !`);
    }
    if (errorCount > 0) {
      toast.error(`❌ ${errorCount} erreur(s) lors de la validation`);
    }
    
    setSelectedIds(new Set());
    loadAdhesions();
  };

  // Rejet en masse
  const handleBulkReject = async () => {
    if (selectedIds.size === 0) {
      toast.error('Veuillez sélectionner au moins un virement');
      return;
    }

    const reason = window.prompt(`Raison du rejet pour ${selectedIds.size} virement(s) :`);
    if (!reason) return;

    setBulkProcessing(true);
    let successCount = 0;
    let errorCount = 0;

    for (const id of selectedIds) {
      try {
        await axios.post(
          `${API_URL}/api/ftpe-adhesion/admin/reject-virement/${id}`,
          { reason },
          { headers }
        );
        successCount++;
      } catch {
        errorCount++;
      }
    }

    setBulkProcessing(false);
    
    if (successCount > 0) {
      toast.success(`🗑️ ${successCount} virement(s) rejeté(s)`);
    }
    if (errorCount > 0) {
      toast.error(`❌ ${errorCount} erreur(s) lors du rejet`);
    }
    
    setSelectedIds(new Set());
    loadAdhesions();
  };

  // Charger les adhésions en attente de virement
  const loadAdhesions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/ftpe-adhesion/admin/virements-pending`, { headers });
      setAdhesions(response.data.adhesions || []);
      setStats(response.data.stats || { pending: 0, validated: 0, rejected: 0 });
    } catch {
      toast.error('Erreur lors du chargement des virements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdhesions();
  }, []);

  // Filtrer les adhésions
  const filteredAdhesions = adhesions.filter(a => {
    const matchesSearch = !searchTerm || 
      (a.raison_sociale || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.virement_auteur || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.virement_reference || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || a.payment_status === filterStatus;

    // Filtre date : virement_date (validés) sinon date de demande
    const refDate = (a.virement_date || a.created_at || '').slice(0, 10);
    const matchesDate = !filterDate || refDate === filterDate;

    return matchesSearch && matchesStatus && matchesDate;
  });

  // Valider un virement
  const handleValidate = async () => {
    if (!validateModal.adhesion) return;
    
    setValidating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/ftpe-adhesion/admin/validate-virement/${validateModal.adhesion.id}`,
        {
          virement_date: virementDate,
          virement_amount: parseFloat(virementAmount),
          virement_auteur: virementAuteur,
          notes: `Virement validé le ${new Date().toLocaleDateString('fr-FR')}`
        },
        { headers }
      );
      
      toast.success(response.data.message || 'Virement validé avec succès !');
      
      if (response.data.bonus_credits) {
        toast.success(`🎁 ${response.data.bonus_credits} crédits bonus attribués !`, { duration: 5000 });
      }
      
      setValidateModal({ open: false, adhesion: null });
      loadAdhesions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  // Rejeter un virement
  const handleReject = async () => {
    if (!rejectModal.adhesion || !rejectReason.trim()) {
      toast.error('Veuillez indiquer une raison de rejet');
      return;
    }
    
    setValidating(true);
    try {
      await axios.post(
        `${API_URL}/api/ftpe-adhesion/admin/reject-virement/${rejectModal.adhesion.id}`,
        { reason: rejectReason },
        { headers }
      );
      
      toast.success('Virement rejeté');
      setRejectModal({ open: false, adhesion: null });
      setRejectReason('');
      loadAdhesions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du rejet');
    } finally {
      setValidating(false);
    }
  };

  // Export CSV des virements validés (avec période optionnelle)
  const handleExportCsv = async () => {
    try {
      const params = {};
      if (exportFrom) params.date_from = exportFrom;
      if (exportTo) params.date_to = exportTo;
      const response = await axios.get(
        `${API_URL}/api/ftpe-adhesion/admin/virements-export-csv`,
        { headers, responseType: 'blob', params }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `virements_ftpe_valides_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch {
      toast.error('Erreur lors de l\'export CSV');
    }
  };

  // Corriger la date / l'auteur d'un virement déjà validé
  const handleEditVirement = async (adhesionId, payload) => {
    try {
      await axios.post(
        `${API_URL}/api/ftpe-adhesion/admin/edit-virement/${adhesionId}`,
        payload,
        { headers }
      );
      toast.success('Virement mis à jour');
      setDetailModal({ open: false, adhesion: null });
      loadAdhesions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Landmark className="w-7 h-7 text-blue-600" />
            Validation des Virements FTPE
          </h2>
          <p className="text-gray-500">Validez les virements reçus pour activer les adhésions</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col">
            <label className="text-[10px] text-gray-500 mb-0.5">Du</label>
            <Input
              type="date"
              value={exportFrom}
              onChange={(e) => setExportFrom(e.target.value)}
              className="w-[140px] h-9"
              data-testid="export-date-from"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] text-gray-500 mb-0.5">Au</label>
            <Input
              type="date"
              value={exportTo}
              onChange={(e) => setExportTo(e.target.value)}
              className="w-[140px] h-9"
              data-testid="export-date-to"
            />
          </div>
          <Button onClick={handleExportCsv} variant="outline" data-testid="virements-export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={loadAdhesions} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Bilan par territoire + récap mensuel */}
      <FTPEVirementBilan headers={headers} />

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-orange-600 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">En attente</p>
                <p className="text-3xl font-bold">{stats.pending}</p>
              </div>
              <Clock className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-green-600 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Validés ce mois</p>
                <p className="text-3xl font-bold">{stats.validated}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-500 to-rose-600 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Rejetés ce mois</p>
                <p className="text-3xl font-bold">{stats.rejected}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtres et Actions en masse */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par entreprise, email, auteur ou référence..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="virements-search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="w-[160px]"
                title="Filtrer par date de virement / demande"
                data-testid="virements-filter-date"
              />
              {filterDate && (
                <Button variant="ghost" size="sm" onClick={() => setFilterDate('')} data-testid="virements-filter-date-clear">
                  ✕
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {['pending_virement', 'paid', 'rejected', 'all'].map(status => (
                <Button
                  key={status}
                  variant={filterStatus === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus(status)}
                  className={filterStatus === status ? 'bg-blue-600' : ''}
                >
                  {status === 'pending_virement' && 'En attente'}
                  {status === 'paid' && 'Validés'}
                  {status === 'rejected' && 'Rejetés'}
                  {status === 'all' && 'Tous'}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Barre d'actions en masse */}
          {filterStatus === 'pending_virement' && adhesions.filter(a => a.payment_status === 'pending_virement').length > 0 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectedIds.size === adhesions.filter(a => a.payment_status === 'pending_virement').length ? deselectAll : selectAll}
                  className="flex items-center gap-2"
                >
                  {selectedIds.size === adhesions.filter(a => a.payment_status === 'pending_virement').length ? (
                    <>
                      <CheckSquare className="h-4 w-4 text-blue-600" />
                      Tout désélectionner
                    </>
                  ) : (
                    <>
                      <Square className="h-4 w-4" />
                      Tout sélectionner
                    </>
                  )}
                </Button>
                
                {selectedIds.size > 0 && (
                  <Badge variant="secondary" className="bg-blue-700 text-white text-sm px-3 py-1 font-semibold">
                    {selectedIds.size} sélectionné(s)
                  </Badge>
                )}
              </div>

              {selectedIds.size > 0 && (
                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleBulkValidate}
                    disabled={bulkProcessing}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {bulkProcessing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCheck className="h-4 w-4 mr-2" />
                    )}
                    Valider ({selectedIds.size})
                  </Button>
                  
                  <Button
                    variant="destructive"
                    onClick={handleBulkReject}
                    disabled={bulkProcessing}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {bulkProcessing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <XCircle className="h-4 w-4 mr-2" />
                    )}
                    Rejeter ({selectedIds.size})
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {['paid', 'all'].includes(filterStatus) && <FtpeCbList search={searchTerm} />}

      {/* Liste des virements */}
      <div className="space-y-4">
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-blue-500 mb-4" />
              <p className="text-gray-500">Chargement des virements...</p>
            </CardContent>
          </Card>
        ) : filteredAdhesions.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Landmark className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucun virement à afficher</h3>
              <p className="text-gray-500">
                {filterStatus === 'pending_virement' 
                  ? 'Tous les virements ont été traités !'
                  : 'Aucun résultat ne correspond à vos critères.'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredAdhesions.map(adhesion => (
            <VirementCard
              key={adhesion.id}
              adhesion={adhesion}
              isSelected={selectedIds.has(adhesion.id)}
              onToggleSelect={() => toggleSelection(adhesion.id)}
              onValidate={(a) => {
                // Pré-remplit l'auteur avec le nom du COOPER'S / adhérent (éditable)
                setVirementAuteur(
                  a.raison_sociale
                  || [a.representant_prenom, a.representant_nom].filter(Boolean).join(' ')
                  || a.representant_nom
                  || a.email
                  || ''
                );
                setValidateModal({ open: true, adhesion: a });
              }}
              onReject={(a) => setRejectModal({ open: true, adhesion: a })}
              onView={(a) => setDetailModal({ open: true, adhesion: a })}
            />
          ))
        )}
      </div>

      {/* Modales validation / rejet / détail */}
      <FTPEVirementModals
        validateModal={validateModal}
        setValidateModal={setValidateModal}
        rejectModal={rejectModal}
        setRejectModal={setRejectModal}
        detailModal={detailModal}
        setDetailModal={setDetailModal}
        virementDate={virementDate}
        setVirementDate={setVirementDate}
        virementAmount={virementAmount}
        setVirementAmount={setVirementAmount}
        virementAuteur={virementAuteur}
        setVirementAuteur={setVirementAuteur}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        validating={validating}
        handleValidate={handleValidate}
        handleReject={handleReject}
        handleEditVirement={handleEditVirement}
      />
    </div>
  );
}
